import React, {useContext, useEffect, useRef, useCallback} from 'react';
import {UserContext} from '../common/UserContext';
import Button from '../common/Button';
import {ChevronRightIcon} from '../common/icons/';
import {ContentContext} from "../common/ContentContext";
import CircularLoader from "../common/CircularLoader";
import Tooltip from "../common/Tooltip";

const SeriesSelect = props => {
    const {className, visible, compressed, onClick} = props;
    const {user, userLoading} = useContext(UserContext);
    const {contentLoading} = useContext(ContentContext);
    const keyStateRef = useRef({ shiftPressed: false, tabPressed: false });

    const focusFirstElementInDrawer = () => {
        const seriesDrawer = document.querySelector('.ch-series-nav-head');
        if (seriesDrawer) {
            const focusableSelectors = [
                'input:not([disabled]):not([type="hidden"])',
                'select:not([disabled])',
                '[tabindex]:not([tabindex="-1"])'
            ];
            const focusable = seriesDrawer.querySelectorAll(focusableSelectors.join(','));
            if (focusable.length > 0) {
                focusable[0].focus();
            }
        }
    };

    const focusLastElementInConcepts = () => {
        const conceptsContainer = document.querySelector('.ch-series-menu-concepts');
        if (conceptsContainer) {
            const focusableSelectors = [
                'a[href]:not([tabindex="-1"])',
                'button:not([disabled]):not([tabindex="-1"])',
                '[tabindex]:not([tabindex="-1"])'
            ];
            const focusable = conceptsContainer.querySelectorAll(focusableSelectors.join(','));

            if (focusable.length > 0) {
                focusable[focusable.length - 1].focus();
            }
        }
    };

    const handleKeyDown = useCallback(e => {
        if (e.key === 'Tab') {
            keyStateRef.current.tabPressed = true;
            keyStateRef.current.shiftPressed = e.shiftKey;
        }
    }, []);

    const handleFocus = () => {
        keyStateRef.current = { tabPressed: false, shiftPressed: false };
        document.addEventListener('keydown', handleKeyDown);
    };

    const handleBlur = () => {
        document.removeEventListener('keydown', handleKeyDown);
        if (!visible) return;

        const { tabPressed, shiftPressed } = keyStateRef.current;
        if (tabPressed) {
            if (shiftPressed) {
                focusLastElementInConcepts();
            } else {
                focusFirstElementInDrawer();
            }
        }
    };

    useEffect(() => {
        // Optional: clean up on unmount just in case
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    return (
        <div className={className ? className : ''}>
            {!compressed &&
                <>
                    <div className="ch-series-select-header">
                        <div className="ch-series-select-content" >
                            <p>My course</p>
                            <p className={`ch-series-select-title ${user.syllabusName?.length > 40 ? ' small' : ''}`}>
                                {user.syllabusName?.length > 67 ? user.syllabusName?.slice(0, 64) + '...' : user.syllabusName}
                            </p>
                        </div>
                        <>
                            <CircularLoader visible={userLoading || contentLoading} pageCentered={false}/>
                            <svg className={'ch-icon-sm'} aria-hidden='true'
                                 dangerouslySetInnerHTML={{__html: '<use xlink:href="#navLessons-icon"/>'}}
                                 viewBox='0 0 128 128'/>
                        </>
                    </div>
                    <Button
                        className={'ch-series-select ch-ignore-click-outside'}
                        aria-controls="course-selection-panel"
                        aria-expanded={visible}
                        onClick={onClick}
                        type={'outline'}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    >
                        <span>
                            See topics
                        </span>
                        <span className={'ch-series-select-icon' + (visible ? ' pull-out-l' : '')}>
                            <ChevronRightIcon/>
                        </span>
                    </Button>
                </>
            }
            {compressed &&
                <Tooltip title={'See course topics'} placement="right" enterNextDelay={100} disableInteractive>
                    <Button
                        className={'ch-series-select ch-ignore-click-outside'}
                        aria-controls="course-selection-panel"
                        aria-expanded={visible}
                        onClick={onClick}
                        type={'outline'}
                        aria-label='See topics'
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    >
                        <svg className={'ch-icon'}
                             dangerouslySetInnerHTML={{__html: '<use xlink:href="#navLessons-icon"/>'}}
                             viewBox='0 0 128 128'/>
                    </Button>
                </Tooltip>
            }
        </div>
    );
};

export default SeriesSelect;